import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Mail, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import ResendVerificationModal from './auth/ResendVerificationModal';

interface EmailVerificationGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const EmailVerificationGuard: React.FC<EmailVerificationGuardProps> = ({ 
  children, 
  redirectTo = '/verify-email' 
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showResendModal, setShowResendModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const isEmailVerified = user?.email_confirmed_at;

  useEffect(() => {
    // If user is not verified and we're not loading, redirect to verification page
    if (!loading && user && !isEmailVerified) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, loading, isEmailVerified, navigate, redirectTo]);

  // Show loading while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user, show children (will be handled by other auth guards)
  if (!user) {
    return <>{children}</>;
  }

  // If email is verified, show children
  if (isEmailVerified) {
    return <>{children}</>;
  }

  // If email is not verified, show verification page
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="h-12 w-12 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Verify Your Email</CardTitle>
          <CardDescription>
            Please verify your email address to access Iron Disciple
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Status */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-medium">Email Address</h3>
                <p className="text-sm text-gray-600">{user.email}</p>
              </div>
            </div>
            <Badge variant="destructive">Unverified</Badge>
          </div>

          {/* Verification Alert */}
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p>Your email address is not verified. Please check your inbox and click the verification link.</p>
                <div className="text-sm">
                  <p className="font-medium">If you don't see the email:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Check your spam/junk folder</li>
                    <li>Make sure you entered the correct email address</li>
                    <li>Click "Resend Email" below to get a new verification link</li>
                  </ul>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={() => setShowResendModal(true)}
              className="w-full"
              disabled={isChecking}
            >
              <Mail className="h-4 w-4 mr-2" />
              {isChecking ? "Sending..." : "Resend Verification Email"}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="w-full"
              disabled={isChecking}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Page
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-gray-600">
            <p>After verifying your email, you'll have full access to:</p>
            <ul className="mt-2 space-y-1">
              <li>• Community discussions</li>
              <li>• Bible study groups</li>
              <li>• Prayer requests</li>
              <li>• User profiles</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <ResendVerificationModal
        isOpen={showResendModal}
        onClose={() => setShowResendModal(false)}
        userEmail={user.email}
      />
    </div>
  );
};

export default EmailVerificationGuard; 